import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import OnboardingPage from "./pages/OnboardingPage";
import HomePage from "./pages/HomePage";
import LiveScoresPage from "./pages/LiveScoresPage";
import LiveMatchRoom from "./pages/LiveMatchRoom";
import ProfilePage from "./pages/ProfilePage";
import MessagesPage from "./pages/MessagesPage";
import ChatPage from "./pages/ChatPage";
import SearchPage from "./pages/SearchPage";
import TeamHubPage from "./pages/TeamHubPage";
import ScoutRadarPage from "./pages/ScoutRadarPage";

// Components
import BottomNav from "./components/BottomNav";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route wrapper
const ProtectedRoute = ({ children, requireOnboarding = true }) => {
    const { isAuthenticated, user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (requireOnboarding && user && !user.role) {
        return <Navigate to="/onboarding" replace />;
    }

    return children;
};

// Layout with Bottom Nav
const MainLayout = ({ children }) => {
    return (
        <div className="viewport-container">
            <div className="flex-1 overflow-hidden">
                {children}
            </div>
            <BottomNav />
        </div>
    );
};

function AppContent() {
    const { isAuthenticated, user } = useAuth();

    // Note: Database seeding is now manual-only for production safety
    // Use POST /api/seed endpoint manually in development if needed

    return (
        <div className="App bg-black min-h-screen">
            <BrowserRouter>
                <Routes>
                    {/* Auth Routes */}
                    <Route path="/login" element={
                        isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
                    } />
                    <Route path="/signup" element={
                        isAuthenticated ? <Navigate to="/" replace /> : <SignupPage />
                    } />
                    <Route path="/onboarding" element={
                        <ProtectedRoute requireOnboarding={false}>
                            {user?.role ? <Navigate to="/" replace /> : <OnboardingPage />}
                        </ProtectedRoute>
                    } />

                    {/* Main App Routes */}
                    <Route path="/" element={
                        <ProtectedRoute>
                            <MainLayout>
                                <HomePage />
                            </MainLayout>
                        </ProtectedRoute>
                    } />
                    <Route path="/scores" element={
                        <ProtectedRoute>
                            <MainLayout>
                                <LiveScoresPage />
                            </MainLayout>
                        </ProtectedRoute>
                    } />
                    <Route path="/match/:matchId" element={
                        <ProtectedRoute>
                            <LiveMatchRoom />
                        </ProtectedRoute>
                    } />
                    <Route path="/team/:teamId" element={
                        <ProtectedRoute>
                            <TeamHubPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/profile" element={
                        <ProtectedRoute>
                            <MainLayout>
                                <ProfilePage />
                            </MainLayout>
                        </ProtectedRoute>
                    } />
                    <Route path="/profile/:userId" element={
                        <ProtectedRoute>
                            <MainLayout>
                                <ProfilePage />
                            </MainLayout>
                        </ProtectedRoute>
                    } />
                    <Route path="/messages" element={
                        <ProtectedRoute>
                            <MainLayout>
                                <MessagesPage />
                            </MainLayout>
                        </ProtectedRoute>
                    } />
                    <Route path="/chat/:conversationId" element={
                        <ProtectedRoute>
                            <ChatPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/search" element={
                        <ProtectedRoute>
                            <MainLayout>
                                <SearchPage />
                            </MainLayout>
                        </ProtectedRoute>
                    } />
                    <Route path="/scout" element={
                        <ProtectedRoute>
                            <MainLayout>
                                <ScoutRadarPage />
                            </MainLayout>
                        </ProtectedRoute>
                    } />

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
            <Toaster position="top-center" />
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App;
